import { AttachEmailOutlined, EditOutlined, ErrorOutline, MailOutline } from "@mui/icons-material";
import EmailAttachmentChips from "components/Email/EmailAttachmentChips";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { RefreshData } from "components/helper";
import DOMPurify from "dompurify";
import { formatDisplayDateTime } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Tab, Tabs } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import { fetchEmailBatch, fetchEmailBatches } from "utils/emailLogService";
import { hasPermission } from "utils/permissionUtils";
import SendAdmissionDocumentsDialog from "./SendAdmissionDocumentsDialog";
import WhatsAppLogs from "./WhatsAppLogs";
import { P } from "const/permissions";

const BatchStatusChip = ({ status, size = "small" }) => {
  const map = {
    SUCCESS: { color: "success", variant: "outlined" },
    FAILED: { color: "error", variant: "filled" },
    PARTIAL: { color: "warning", variant: "filled" },
  };
  const { color, variant } = map[status] || { color: "default", variant: "outlined" };
  return <Chip size={size} label={status || "UNKNOWN"} color={color} variant={variant} />;
};

const RecipientStatusChip = ({ status }) => (
  <Chip
    size="small"
    label={status || "UNKNOWN"}
    color={status === "SUCCESS" ? "success" : "error"}
    variant={status === "SUCCESS" ? "outlined" : "filled"}
    sx={{ fontSize: "0.7rem", height: 20 }}
  />
);

const MetaField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {label}
    </Typography>
    <Typography variant="body2" className="break-words">
      {value || "—"}
    </Typography>
  </Box>
);

const EmailBatchExpandedPanel = ({ batchId }) => {
  const [recipientSearch, setRecipientSearch] = useState("");
  const { data: batch, isFetching } = useQuery(
    ["email-batch", batchId],
    () => fetchEmailBatch(batchId),
    { enabled: !!batchId, refetchOnWindowFocus: false, keepPreviousData: true }
  );

  if (isFetching && !batch) {
    return (
      <Box className="flex items-center justify-center py-8">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!batch) return null;

  const failedRecipients = (batch.recipients || []).filter((r) => r.status === "FAILED");
  const filteredRecipients = (batch.recipients || []).filter((r) => {
    if (!recipientSearch.trim()) return true;
    const q = recipientSearch.toLowerCase();
    return (
      r.recipientEmail?.toLowerCase().includes(q) || r.recipientName?.toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ p: 2, bgcolor: "background.default" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} flexWrap="wrap">
        <MetaField label="From" value={batch.fromEmail} />
        <MetaField label="Sent by" value={batch.sentByName || batch.sentByEmail} />
        <MetaField label="Provider" value={batch.provider} />
        <MetaField label="Sent at" value={formatDisplayDateTime(batch.sentAt)} />
      </Stack>

      {(batch.ccRecipients?.length > 0 || batch.bccRecipients?.length > 0) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            {batch.ccRecipients?.length > 0 && (
              <MetaField label="CC" value={batch.ccRecipients.join(", ")} />
            )}
            {batch.bccRecipients?.length > 0 && (
              <MetaField label="BCC" value={batch.bccRecipients.join(", ")} />
            )}
          </Stack>
        </>
      )}

      {(batch.tags || []).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            TAGS
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
            {batch.tags.map((tag) => (
              <Chip key={tag} size="small" label={tag} variant="outlined" />
            ))}
          </Stack>
        </>
      )}

      {batch.attachmentKeys?.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            ATTACHMENTS
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
            <EmailAttachmentChips
              logId={batch.recipients?.[0]?.id}
              attachmentKeys={batch.attachmentKeys}
            />
          </Stack>
        </>
      )}

      {failedRecipients.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box className="flex gap-2 p-3 rounded-md bg-red-50 text-red-700 border border-red-100">
            <ErrorOutline fontSize="small" className="shrink-0 mt-0.5" />
            <Typography variant="body2">
              {failedRecipients.length} recipient{failedRecipients.length > 1 ? "s" : ""} failed to
              receive this email.
            </Typography>
          </Box>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Box className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          RECIPIENTS ({batch.totalCount})
        </Typography>
        {batch.totalCount > 5 && (
          <TextField
            size="small"
            placeholder="Search recipients…"
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
            sx={{ width: { xs: "100%", sm: 240 } }}
          />
        )}
      </Box>
      <Box className="divide-y divide-slate-100 max-h-48 overflow-y-auto rounded border border-app-border bg-app-surface">
        {filteredRecipients.map((r) => (
          <Box key={r.id} className="flex items-center gap-3 px-3 py-2">
            <Box className="min-w-0 flex-1">
              {r.recipientName && r.recipientName !== "External" && (
                <Typography variant="body2" fontWeight={500} className="truncate">
                  {r.recipientName}
                </Typography>
              )}
              <Typography
                variant={r.recipientName && r.recipientName !== "External" ? "caption" : "body2"}
                color="text.secondary"
                className="truncate block"
              >
                {r.recipientEmail}
              </Typography>
              {r.errorMessage && (
                <Typography variant="caption" color="error" className="truncate block">
                  {r.errorMessage}
                </Typography>
              )}
            </Box>
            <RecipientStatusChip status={r.status} />
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        MESSAGE
      </Typography>
      <Box
        className="w-1/2 mx-auto text-sm text-app-text leading-relaxed mt-2 p-4 rounded-lg border border-app-border bg-app-surface"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(batch.body || "<p>No body captured.</p>"),
        }}
      />
    </Box>
  );
};

const Emails = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ pageIndex: 0, pageSize: 20, search: "" });
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const canComposeEmail = hasPermission(P.EMAIL.MESSAGE_COMPOSE);
  const canComposeWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_SEND);
  const canCompose = canComposeEmail || canComposeWhatsApp;
  const canSeeEmail = hasPermission(P.EMAIL.MESSAGE_READ);
  const canSeeWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_READ);
  const [channelTab, setChannelTab] = useState(canSeeEmail ? "email" : "whatsapp");

  const {
    data: batchesData,
    isFetching,
    refetch,
  } = useQuery(
    ["email-batches", filters],
    () =>
      fetchEmailBatches({
        page: filters.pageIndex,
        size: filters.pageSize,
        search: filters.search,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: canSeeEmail && channelTab !== "whatsapp",
    }
  );

  const batches = batchesData?.content || [];
  const totalItems = batchesData?.totalItems || 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: "subject",
        header: "Subject",
        size: 280,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <Typography variant="body2" fontWeight={600} className="truncate">
              {row.original.subject || "(No subject)"}
            </Typography>
            {(row.original.tags || []).length > 0 && (
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                {row.original.tags.slice(0, 2).map((tag) => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 18 }}
                  />
                ))}
              </Stack>
            )}
          </div>
        ),
      },
      {
        accessorKey: "batchStatus",
        header: "Status",
        size: 110,
        Cell: ({ row }) => <BatchStatusChip status={row.original.batchStatus} />,
      },
      {
        accessorKey: "totalCount",
        header: "Recipients",
        size: 100,
        Cell: ({ cell }) => <Chip size="small" variant="outlined" label={cell.getValue() ?? 0} />,
      },
      {
        accessorKey: "sentByName",
        header: "Sent by",
        size: 160,
        Cell: ({ row }) => (
          <span className="text-sm truncate block">
            {row.original.sentByName || row.original.sentByEmail || "—"}
          </span>
        ),
      },
      {
        accessorKey: "fromEmail",
        header: "From",
        size: 180,
        Cell: ({ cell }) => (
          <span className="text-sm truncate block">{cell.getValue() || "—"}</span>
        ),
      },
      {
        accessorKey: "provider",
        header: "Provider",
        size: 120,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "sentAt",
        header: "Sent",
        size: 160,
        Cell: ({ cell }) => (
          <span className="text-sm whitespace-nowrap">
            {formatDisplayDateTime(cell.getValue())}
          </span>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: batches,
    getRowId: (row) => String(row.batchId),
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    enableExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / filters.pageSize) || 0,
    onPaginationChange: (updater) => {
      setFilters((prev) => {
        const pagination =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, pageIndex: pagination.pageIndex, pageSize: pagination.pageSize };
      });
    },
    onGlobalFilterChange: (value) => {
      setFilters((prev) => ({ ...prev, search: value ?? "", pageIndex: 0 }));
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-numbers", "subject"],
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    paginationDisplayMode: "pages",
    state: {
      isLoading: isFetching,
      showProgressBars: isFetching,
      globalFilter: filters.search || "",
      pagination: { pageIndex: filters.pageIndex, pageSize: filters.pageSize },
    },
    ...getMrtTableThemeProps({ maxHeight: "calc(100vh - 320px)" }),
    muiTableBodyRowProps: ({ row }) => ({
      sx: getMrtBodyRowSx(row),
    }),
    renderDetailPanel: ({ row }) => <EmailBatchExpandedPanel batchId={row.original.batchId} />,
    renderEmptyRowsFallback: () => (
      <Box className="flex flex-col items-center justify-center py-16 text-center">
        <MailOutline className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
        <Typography fontWeight={600}>No emails found</Typography>
        <Typography variant="body2" color="text.secondary">
          {isFetching ? "Loading…" : "Try adjusting your search."}
        </Typography>
      </Box>
    ),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <SendAdmissionDocumentsDialog open={docPickerOpen} onClose={() => setDocPickerOpen(false)} />

      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2 text-app-text">
                  <MailOutline fontSize="small" />
                  Messages ({channelTab === "whatsapp" ? "WhatsApp" : totalItems})
                  <RefreshData refreshData={refetch} />
                </h3>
                <p className="text-sm text-app-text-muted mt-0.5">
                  Email and WhatsApp sent from the dashboard. Expand a row for who received it and
                  who sent it.
                </p>
              </div>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {canCompose && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditOutlined />}
                    onClick={() => navigate("/admin/emails/compose")}
                  >
                    Compose message
                  </Button>
                )}
                {canComposeEmail && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachEmailOutlined />}
                    onClick={() => setDocPickerOpen(true)}
                  >
                    Send admission documents
                  </Button>
                )}
              </Stack>
            </div>
          </div>

          <div className="px-4 pb-4">
            {canSeeEmail && canSeeWhatsApp && (
              <Tabs value={channelTab} onChange={(_, value) => setChannelTab(value)} sx={{ mb: 2 }}>
                <Tab value="email" label="Email" />
                <Tab value="whatsapp" label="WhatsApp" />
              </Tabs>
            )}
            {channelTab === "whatsapp" && canSeeWhatsApp ? (
              <WhatsAppLogs />
            ) : (
              <MaterialReactTable table={table} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Emails;
